import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio, Cpu, MessageSquare, ShieldCheck, Users, Activity } from "lucide-react";

const heroBg = "https://images.unsplash.com/photo-1686161933916-89856015a815?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-[#272E3A]"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#0A0C10]/80" />
        <div className="absolute inset-0 tactical-grid opacity-40" />
        <div className="absolute inset-0 scanlines pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6 fade-in">
              <span className="inline-block w-2 h-2 bg-[#FF5500] rounded-full animate-pulse-dot" />
              <span className="overline">East Coast · Regions · USA</span>
            </div>
            <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-6 fade-in">
              Off-grid mesh<br />
              for when the<br />
              <span className="text-[#FF5500]">grid goes dark.</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed mb-10 fade-in">
              Eastern US Meshcore is an initiative to bring all east coast Meshcore communities together in order to visualize the traffic between states from a single site.
            </p>
            <div className="flex flex-wrap gap-4 fade-in">
              <Link
                to="/devices"
                data-testid="hero-get-started-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold tracking-wide transition-colors"
              >
                Get Connected <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/meshcore"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-[#272E3A] bg-[#13161C]/60 hover:bg-[#1C2028] text-white font-semibold tracking-wide transition-colors"
              >
                What is MeshCore?
              </Link>
            </div>
          </div>
        </div>

        {/* Hero stat strip */}
        <div className="relative border-t border-[#272E3A] bg-[#0A0C10]/90 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 font-mono">
            {[
              { k: "910.525", l: "MHz · Frequency" },
              { k: "62.5", l: "kHz · Bandwidth" },
              { k: "SF7", l: "Spread Factor" },
              { k: "USA/CA", l: "Region Preset" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-[#FF5500] text-2xl lg:text-3xl font-bold">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline mb-3">/01 — Onboarding</div>
            <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              How it works
            </h2>
          </div>
          <div className="text-slate-400 max-w-md text-sm">
            Three steps. About fifteen minutes. You don't need a license, a tower, or a tech degree.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {[
            {
              n: "01",
              title: "Get a Radio",
              icon: Radio,
              body: "LoRa mesh devices start around $30. Recommended boards: Heltec V3, RAK4631, T-Beam, Xiao S3 WIO.",
              cta: { to: "/devices", label: "See devices →" },
            },
            {
              n: "02",
              title: "Install & Configure",
              icon: Cpu,
              body: "Flash MeshCore firmware via the web flasher, apply our East Coast preset, pair via Bluetooth.",
              cta: { to: "/settings", label: "Settings →" },
            },
            {
              n: "03",
              title: "Start Messaging",
              icon: MessageSquare,
              body: "Talk on the Public channel or spin up your own Private channel. Range extends as the mesh grows.",
              cta: { to: "", label: "" },
            },
          ].map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.n}
                data-testid={`how-it-works-step-${step.n}`}
                className="group relative bg-[#13161C] border border-[#272E3A] p-8 hover:border-[#FF5500] transition-colors"
              >
                <div className="flex items-start justify-between mb-8">
                  <Icon className="w-8 h-8 text-[#FF5500]" strokeWidth={1.5} />
                  <span className="font-mono text-xs text-[#94A3B8] tracking-widest">STEP_{step.n}</span>
                </div>
                <h3 className="font-display font-bold text-white text-2xl mb-3">{step.title}</h3>
                <p className="text-slate-400 mb-6 leading-relaxed">{step.body}</p>
                <Link
                  to={step.cta.to}
                  className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#FF5500] hover:text-[#FF7326]"
                >
                  {step.cta.label}
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="border-y border-[#272E3A] bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <div className="overline mb-3">/02 — Mission</div>
              <h2 className="font-display font-black text-white text-3xl sm:text-4xl tracking-tight">
                A network the community owns.
              </h2>
            </div>
            <div className="lg:col-span-2 space-y-8">
              <p className="text-slate-300 text-lg leading-relaxed">
                Each Meshcore region is built and operated entirely by local volunteers. They deploy repeaters on rooftops,
                ridgelines, and church steeples. They share configurations, troubleshoot together, and keep messaging
                running when the towers go quiet.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { icon: ShieldCheck, k: "Emergency Ready", v: "Works when traditional networks fail. No power or internet required." },
                  { icon: Users, k: "Community Owned", v: "Built by volunteers. No corporations. No subscriptions. Ever." },
                  { icon: Activity, k: "Always Listening", v: "Observer nodes feed live network telemetry to our central MQTT broker." },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.k} className="border-l-2 border-[#FF5500] pl-4">
                      <Icon className="w-5 h-5 text-[#FF5500] mb-3" strokeWidth={1.5} />
                      <div className="font-display font-bold text-white text-base mb-1">{b.k}</div>
                      <div className="text-sm text-slate-400">{b.v}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="relative bg-[#13161C] border border-[#272E3A] p-10 lg:p-16 overflow-hidden">
          <div className="absolute inset-0 tactical-grid opacity-30 pointer-events-none" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="overline mb-4">/03 — Join</div>
              <h2 className="font-display font-black text-white text-3xl lg:text-5xl tracking-tight mb-4">
                Drop a note.<br />
                Grow the mesh.
              </h2>
              <p className="text-slate-400 text-lg max-w-xl">
                Discord is where it all happens — config help, network maps, parts for sale, repeater coordination.
                Drop in and say hello.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="https://discord.gg/3TPXsHBv6t"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-discord-btn"
                className="px-6 py-4 bg-[#FF5500] hover:bg-[#FF7326] text-white font-semibold flex items-center justify-between transition-colors"
              >
                <span>Join the Discord</span> <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="mailto:warrenton.meshcore@gmail.com"
                data-testid="cta-email-btn"
                className="px-6 py-4 border border-[#272E3A] hover:border-[#FF5500] hover:bg-[#1C2028] text-white font-semibold flex items-center justify-between transition-colors"
              >
                <span>Email the Team</span> <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
